<template>
  <article class="dashboard-card preika-insight-card preika-close-cockpit">
    <div class="preika-insight-heading preika-insight-heading--with-actions">
      <div>
        <p class="preika-insight-heading__eyebrow">MONTHLY CLOSE</p>
        <h2>月次締めコックピット</h2>
        <!--
          説明文は実態に合わせる。以前は「提出管理に登録されているスタッフ」で、
          そのとおり記録がある人しか並ばなかった。まだ何も出していない人こそ
          催促が要るのに現れない一覧だった。2026-08-17 に作り直した。
        -->
        <p class="preika-insight-heading__description">
          承認を担当するスタッフの進捗状況を、提出の有無に関わらず確認できます。
        </p>
      </div>
      <span class="preika-insight-badge preika-insight-badge--neutral">
        {{ formattedYearMonth }}
      </span>
    </div>

    <div v-if="loading" class="preika-insight-loading">
      <span class="preika-insight-loading__spinner"></span>
      月次締め状況を集計中
    </div>

    <div v-else-if="error" class="preika-insight-error" role="alert">
      <div>
        <strong>月次締め状況を取得できませんでした</strong>
        <small>既存の各一覧画面はそのままご利用いただけます。</small>
      </div>
      <button type="button" @click="$emit('retry')">再取得</button>
    </div>

    <!--
      **0名のときは空の表を出さない。**このカードに並ぶのは、自分が責任者の
      プロジェクトのメンバーだけ。責任者でない事務担当者には「対象者0名・
      0%」と空の表が出て、会社の締めがどこで見られるかも出ていなかった（束34）。
      会社全体の締めを見る画面への道を、開ける人にだけ出す。

      リンクの行は、字のあいだに空きが入らないよう1行に詰めて書く
      （タグのあいだで改行すると、画面の文に半角の空きが混ざる）。
    -->
    <div v-else-if="totalUsers === 0" class="preika-insight-empty preika-close-empty">
      <span class="preika-insight-empty__icon"><CIcon name="cil-list"/></span>
      <div>
        <strong>{{ formattedYearMonth }}の月次締め対象のスタッフはいません</strong>
        <p>このカードには、あなたがプロジェクト責任者になっているプロジェクトのスタッフが表示されます。</p>
        <p v-if="companyCloseLinks.length" class="preika-close-empty-links">全社の状況は<template v-for="(link, index) in companyCloseLinks"><span v-if="index > 0" :key="link.route + '-sep'">または</span><a :key="link.route" href="#" @click.prevent="openList(link.route)">{{ link.label }}</a></template>で確認できます。</p>
      </div>
    </div>

    <template v-else>
      <div class="preika-close-overview">
        <div class="preika-progress-ring" :style="ringStyle">
          <div>
            <strong>{{ completionRate }}%</strong>
            <small>承認完了</small>
          </div>
        </div>

        <div class="preika-close-metrics">
          <div><small>対象者</small><strong>{{ value.totalUsers || 0 }}</strong><span>名</span></div>
          <div><small>締め完了</small><strong>{{ value.completedUsers || 0 }}</strong><span>名</span></div>
          <div><small>未提出</small><strong>{{ value.unsubmittedCount || 0 }}</strong><span>件</span></div>
          <div><small>差戻し</small><strong>{{ value.rejectedCount || 0 }}</strong><span>件</span></div>
          <div><small>承認待ち</small><strong>{{ value.pendingCount || 0 }}</strong><span>件</span></div>
          <div class="preika-close-metrics__overdue"><small>3日以上滞留</small><strong>{{ value.overdueCount || 0 }}</strong><span>件</span></div>
        </div>
      </div>

      <div class="preika-close-filterbar">
        <div class="preika-close-tabs" role="tablist" aria-label="月次締めの絞り込み">
          <button
            v-for="option in filterOptions"
            :key="option.value"
            type="button"
            :class="{ active: filter === option.value }"
            @click="selectFilter(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <!--
          **絞り込みはサーバへ渡す。**
          画面で絞ると、いま載っている20人だけが対象になり、
          2ページ目にいる人を探しても「いません」と出る。
          件数は全員から数えているので、母数も食い違う。
        -->
        <label class="preika-close-search">
          <CIcon name="cil-magnifying-glass"/>
          <input
            v-model.trim="query"
            type="search"
            placeholder="氏名・部署を検索"
            @input="scheduleSearch"
            @keyup.enter="applyCondition"
          >
        </label>
      </div>

      <!--
        1000人規模では1ページに収まらない。**何人目まで見ているかを必ず出す。**

        絞り込み（未完了・滞留…）と検索は**サーバ側で全員から**行う。
        「他 N名」は絞り込んだ結果のうち、まだ見ていない人数。
        上の母数（対象者・未提出…）は絞り込みに関係なく全員ぶん。
      -->
      <div v-if="paged" class="attendance-overview__pager">
        <button
          type="button"
          class="attendance-overview__pager-button"
          :disabled="!hasPrevious"
          @click="$emit('page', Math.max(offset - pageSize, 0))"
        >前へ</button>
        <span class="attendance-overview__pager-label">
          {{ totalUsers }}名中 {{ firstIndex }}〜{{ lastIndex }}名
          <template v-if="hiddenUsers > 0">（他 {{ hiddenUsers }}名）</template>
        </span>
        <button
          type="button"
          class="attendance-overview__pager-button"
          :disabled="!hasNext"
          @click="$emit('page', offset + pageSize)"
        >次へ</button>
      </div>

      <div v-if="filteredUsers.length" class="preika-status-matrix-wrap">
        <table class="preika-status-matrix">
          <thead>
            <tr>
              <th>スタッフ</th>
              <th v-for="document in documentHeaders" :key="document.documentType">
                {{ document.documentName }}
              </th>
              <th>進捗</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in filteredUsers" :key="user.userId">
              <th scope="row">
                <span class="preika-matrix-person">
                  <span class="preika-matrix-person__avatar">{{ userInitial(user.userName) }}</span>
                  <span><strong>{{ user.userName }}</strong><small>{{ user.departmentName || '未所属' }}</small></span>
                </span>
              </th>
              <td v-for="document in user.documents" :key="document.documentType">
                <!--
                  押すと、締めの月と氏名を渡して一覧を開く。月を渡さないと、
                  一覧は自分の既定の月（前月）で開き、締めの日を過ぎた今月の
                  締めを見ていても前月の一覧が出る。
                -->
                <a
                  v-if="document.applicable !== false"
                  href="#"
                  class="preika-document-state"
                  :class="'preika-document-state--' + statusTone(document)"
                  :title="documentTitle(document)"
                  @click.prevent="openList(document.route || '/dashboard', user.userName)"
                >
                  <CIcon :name="statusIcon(document)"/>
                  <span>{{ compactStatusName(document) }}</span>
                  <small v-if="Number(document.waitingDays) >= 3">{{ document.waitingDays }}日</small>
                </a>
                <span
                  v-else
                  class="preika-document-state preika-document-state--unsubmitted"
                  :title="document.documentName + ': 対象外'"
                >
                  <CIcon name="cil-ban"/>
                  <span>対象外</span>
                </span>
              </td>
              <td>
                <span class="preika-user-close-progress" :class="{ complete: user.complete }">
                  <strong>{{ user.approvedCount || 0 }}/{{ user.totalCount || 0 }}</strong>
                  <small>{{ user.complete ? '完了' : '対応中' }}</small>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="preika-insight-empty">
        <span class="preika-insight-empty__icon"><CIcon name="cil-list"/></span>
        <div>
          <strong>条件に一致するスタッフはいません</strong>
          <p>絞り込みまたは検索条件を変更してください。</p>
        </div>
      </div>
    </template>
  </article>
</template>

<script>
import {
  clampInsightPercent,
  documentStatusTone,
  formatInsightYearMonth,
  openListWith
} from '../../utils/dashboardInsights'
import { canAccessPath } from '../../utils/accessPolicy'

export default {
  name: 'DashboardCloseCockpit',

  props: {
    summary: {
      type: Object,
      default: () => ({})
    },
    /** 本人の役割・会社の機能・権限。会社全体の締めの画面を開けるかを決める。 */
    accessContext: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      filter: 'INCOMPLETE',
      query: '',
      // 打つたびに投げない。打ち終わってから1回だけ投げる。
      searchTimer: null,
      filterOptions: [
        { value: 'INCOMPLETE', label: '未完了' },
        { value: 'OVERDUE', label: '滞留' },
        { value: 'REJECTED', label: '差戻し' },
        { value: 'PENDING', label: '承認待ち' },
        { value: 'COMPLETE', label: '完了' },
        { value: 'ALL', label: 'すべて' }
      ]
    }
  },

  computed: {
    value () {
      return this.summary || {}
    },

    users () {
      return Array.isArray(this.value.users) ? this.value.users : []
    },

    /*
     * ページ操作。**数はサーバが返したものだけを使う。**
     *
     * 母数（totalUsers）は全員ぶん、users はこのページぶん。画面で
     * 数え直すと、上のタイルの数字と食い違う。
     */
    totalUsers () {
      return Number(this.value.totalUsers) || 0
    },
    pageSize () {
      return Number(this.value.pageSize) || 0
    },
    offset () {
      return Number(this.value.offset) || 0
    },
    hiddenUsers () {
      return Number(this.value.hiddenUsers) || 0
    },
    /** 1ページに収まっていないときだけ、ページ操作を出す。 */
    paged () {
      return this.pageSize > 0 && this.totalUsers > this.users.length
    },
    firstIndex () {
      return this.users.length ? this.offset + 1 : 0
    },
    lastIndex () {
      return this.offset + this.users.length
    },
    hasPrevious () {
      return this.offset > 0
    },
    hasNext () {
      return this.lastIndex < this.totalUsers
    },

    completionRate () {
      return Math.round(clampInsightPercent(this.value.completionRate) * 10) / 10
    },

    ringStyle () {
      return { '--preika-ring-progress': `${this.completionRate}%` }
    },

    formattedYearMonth () {
      return formatInsightYearMonth(this.value.targetYearMonth)
    },

    /** 会社全体の締めを見る画面のうち、開けるもの（左メニューと同じ判定）。 */
    companyCloseLinks () {
      return [
        { route: '/admin/total/list', label: '月次集計' },
        { route: '/admin/roster/list', label: '勤務表一覧' }
      ].filter(link => canAccessPath(this.accessContext, link.route))
    },

    documentHeaders () {
      return this.users.length && Array.isArray(this.users[0].documents)
        ? this.users[0].documents
        : []
    },

    /*
     * **絞り込みはサーバ側で済んでいる。ここでは絞らない。**
     *
     * 以前はここで this.users を絞っていた。載っているのは1ページ
     * （20人）だけなので、2ページ目にいる人を探しても「いません」と出た。
     * 件数は全員から数えているため母数も食い違っていた。
     */
    filteredUsers () {
      return this.users
    }
  },

  methods: {
    /** 締めの月（と氏名）を渡して一覧を開く。 */
    openList (route, userName) {
      openListWith(this.$router, route, this.value.targetYearMonth, userName)
    },

    /** チップを押したら、その区分でサーバから取り直す。 */
    selectFilter (value) {
      if (this.filter === value) return
      this.filter = value
      this.applyCondition()
    },

    /*
     * 打つたびに投げない。打ち終わってから1回だけ投げる。
     * 1文字ごとに投げると、1000人規模では検索のたびに全件を走査する。
     */
    scheduleSearch () {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null
        this.applyCondition()
      }, 400)
    },

    /*
     * 絞り込みを変えたら先頭のページから取り直す。
     *
     * offset を持ったままだと、3ページ目を見ている状態で絞った結果が
     * 1ページしか無いときに空で表示される。
     */
    applyCondition () {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
      this.$emit('condition', { query: this.query, state: this.filter })
    },

    statusTone (document) {
      return documentStatusTone(
        document.status,
        document.waitingDays,
        document.applicable
      )
    },

    statusIcon (document) {
      return ({
        approved: 'cil-check-circle',
        rejected: 'cil-warning',
        pending: 'cil-clock',
        overdue: 'cil-clock',
        unsubmitted: 'cil-ban',
        excluded: 'cil-ban'
      })[this.statusTone(document)] || 'cil-ban'
    },

    compactStatusName (document) {
      if (this.statusTone(document) === 'overdue') return '滞留'
      if (this.statusTone(document) === 'excluded') return '対象外'
      return document.statusName || '未登録'
    },

    documentTitle (document) {
      const detail = document.updatedAt ? ` / ${document.updatedAt}` : ''
      return `${document.documentName}: ${document.statusName || '未登録'}${detail}`
    },

    userInitial (name) {
      return String(name || '人').substring(0, 1)
    }
  }
}
</script>

<style scoped>
a.preika-document-state{text-decoration:none}
.preika-close-empty-links{margin-top:8px}
.preika-close-empty-links a{margin:0 2px;color:#0a66b2;font-weight:700}
</style>
